import type { DrawerTabId } from '../core/types';

const STORAGE_PREFIX = 'lectura-next-ui-state-v1';

export interface DrawerUIState {
  open: boolean;
  tab: DrawerTabId;
  /** Side-by-side layout: user width in px (clamped at runtime). */
  drawerWidthPx?: number;
  /** Stacked layout: user drawer band height in px (clamped at runtime). */
  stackedDrawerHeightPx?: number;
}

function storageKey(presId: string): string {
  return `${STORAGE_PREFIX}:${String(presId || '').trim() || 'default'}`;
}

export function loadDrawerUIState(presId: string): DrawerUIState {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { open: false, tab: 'chat' };
  }
  try {
    const raw = window.localStorage.getItem(storageKey(presId));
    if (!raw) return { open: false, tab: 'chat' };
    const parsed = JSON.parse(raw) as Partial<DrawerUIState> | null;
    const open = !!parsed?.open;
    let tab: DrawerTabId = 'chat';
    if (parsed?.tab === 'notes') tab = 'notes';
    else if (parsed?.tab === 'speaker') tab = 'speaker';
    const drawerWidthPx =
      typeof parsed?.drawerWidthPx === 'number' && Number.isFinite(parsed.drawerWidthPx)
        ? parsed.drawerWidthPx
        : undefined;
    const stackedDrawerHeightPx =
      typeof parsed?.stackedDrawerHeightPx === 'number' && Number.isFinite(parsed.stackedDrawerHeightPx)
        ? parsed.stackedDrawerHeightPx
        : undefined;
    return { open, tab, drawerWidthPx, stackedDrawerHeightPx };
  } catch {
    return { open: false, tab: 'chat' };
  }
}

export function saveDrawerUIState(
  presId: string,
  next: Partial<DrawerUIState> & { resetDrawerWidth?: boolean; resetStackedDrawerHeight?: boolean }
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const current = loadDrawerUIState(presId);
  let drawerWidthPx = current.drawerWidthPx;
  if (next.resetDrawerWidth) {
    drawerWidthPx = undefined;
  } else if (typeof next.drawerWidthPx === 'number' && Number.isFinite(next.drawerWidthPx)) {
    drawerWidthPx = next.drawerWidthPx;
  }
  let stackedDrawerHeightPx = current.stackedDrawerHeightPx;
  if (next.resetStackedDrawerHeight) {
    stackedDrawerHeightPx = undefined;
  } else if (typeof next.stackedDrawerHeightPx === 'number' && Number.isFinite(next.stackedDrawerHeightPx)) {
    stackedDrawerHeightPx = next.stackedDrawerHeightPx;
  }
  const merged: DrawerUIState = {
    open: typeof next.open === 'boolean' ? next.open : current.open,
    tab:
      next.tab === 'notes' || next.tab === 'speaker' || next.tab === 'chat'
        ? next.tab
        : current.tab,
    drawerWidthPx,
    stackedDrawerHeightPx
  };
  try {
    window.localStorage.setItem(storageKey(presId), JSON.stringify(merged));
  } catch {
    // Ignore persistence errors (private mode/full quota).
  }
}
